import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from './useApi';
import { useWebSocket } from './useWebSocket';

// Eventos que cambian el resumen: se recarga (máximo una vez cada 2 s)
const RELOAD_TYPES = ['portal_event', 'integration_status', 'presence_changed', 'conversation_assigned',
  'conversation_closed', 'conversation_queued', 'conversation_requeued'];
const MIN_RELOAD_MS = 2000;

// Duración de las luces en vivo
export const BUSY_TTL_MS = 120000;   // "procesando" sin fin de turno: se apaga sola
export const FLASH_MS = 1500;        // destello al terminar un turno
export const CLIP_MS = 8000;         // luz de clip tras un error o rechazo

/**
 * Datos de supervisión en vivo (Fase 9): resumen de /api/admin/supervision y
 * luces de los agentes de IA. La actividad de la IA se aplica en memoria (sin
 * recargar el resumen); el resto de eventos provoca una recarga agrupada.
 *
 * @param {(msg: object, data: object|null) => void} [onLive] recibe cada mensaje
 *        del WebSocket junto con el resumen previo (para animaciones)
 */
export function useSupervision(onLive) {
  const { apiFetch } = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // doing: agente → { intent, session_id } del turno en curso o del último
  const [live, setLive] = useState({ busy: {}, flash: {}, clip: {}, doing: {} });
  const [now, setNow] = useState(Date.now());
  const pendingRef = useRef(null);
  const dataRef = useRef(null);
  const onLiveRef = useRef(onLive);
  onLiveRef.current = onLive;

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/admin/supervision');
      dataRef.current = d;
      setData(d);
      // El backend sabe quién sigue procesando (p. ej. al abrir la página a mitad de un turno)
      setLive(l => {
        const busy = {};
        const doing = { ...l.doing };
        d.ai_agents.forEach(a => {
          if (a.busy_since) busy[a.id] = new Date(a.busy_since).getTime();
          if (a.current) doing[a.id] = a.current;
          else if (!doing[a.id] && a.last_session_id) doing[a.id] = { intent: a.last_intent, session_id: a.last_session_id };
        });
        return { ...l, busy, doing };
      });
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [apiFetch]);

  const scheduleReload = useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = setTimeout(() => { pendingRef.current = null; load(); }, MIN_RELOAD_MS);
  }, [load]);

  useEffect(() => {
    load();
    const refresh = setInterval(load, 30000);             // "hace X min" y contadores
    const tick = setInterval(() => setNow(Date.now()), 1000); // apagar luces vencidas
    return () => { clearInterval(refresh); clearInterval(tick); clearTimeout(pendingRef.current); };
  }, [load]);

  const applyAiEvent = useCallback((e, count) => {
    const agent = e.actor_id;
    const t = Date.now();
    setLive(l => {
      const busy = { ...l.busy };
      const flash = { ...l.flash };
      const clip = { ...l.clip };
      const doing = { ...l.doing };
      if (e.type === 'ai_agent.turn_started' || e.type === 'ai_agent.turn_finished') {
        doing[agent] = { intent: e.payload?.intent || doing[agent]?.intent || null, session_id: e.session_id || null };
      }
      if (e.type === 'ai_agent.turn_started') busy[agent] = t;
      if (e.type === 'ai_agent.turn_finished') { delete busy[agent]; flash[agent] = t; }
      if (e.type === 'ai_agent.error' || e.type === 'ai_agent.policy_rejected') { delete busy[agent]; clip[agent] = t; }
      return { busy, flash, clip, doing };
    });
    const d = dataRef.current;
    if (!d) return;
    if (!d.ai_agents.some(a => a.id === agent)) { scheduleReload(); return; }   // agente nuevo
    const isError = e.type === 'ai_agent.error' || e.type === 'ai_agent.policy_rejected';
    if (e.type !== 'ai_agent.turn_finished' && !isError) return;
    const next = {
      ...d,
      ai_agents: d.ai_agents.map(a => a.id !== agent ? a : {
        ...a,
        turns_15m: a.turns_15m + (e.type === 'ai_agent.turn_finished' ? count : 0),
        errors_1h: a.errors_1h + (isError ? count : 0),
        last_turn_at: e.type === 'ai_agent.turn_finished' ? e.created_at : a.last_turn_at,
        last_error_at: isError ? e.created_at : a.last_error_at,
      }),
      alerts: isError && e.id ? [e, ...d.alerts].slice(0, 30) : d.alerts,
    };
    dataRef.current = next;
    setData(next);
  }, [scheduleReload]);

  const wsStatus = useWebSocket(useCallback((msg) => {
    onLiveRef.current?.(msg, dataRef.current);
    if (msg.type === 'portal_event' && msg.data?.actor_kind === 'ai_agent') {
      applyAiEvent(msg.data, msg.coalesced || 1);
      return;
    }
    if (RELOAD_TYPES.includes(msg.type)) scheduleReload();
  }, [applyAiEvent, scheduleReload]));

  return { data, error, live, now, wsStatus, reload: load };
}

/** Estado de las luces de un agente de IA en este momento. */
export function agentLights(live, id, now) {
  const busySince = live.busy[id];
  return {
    busy: !!busySince && now - busySince < BUSY_TTL_MS,
    flash: !!live.flash[id] && now - live.flash[id] < FLASH_MS,
    clip: !!live.clip[id] && now - live.clip[id] < CLIP_MS,
  };
}
